// The price board — every event on one page, no status tabs. Each event
// card carries a vertical status ribbon (LIVE / UPCOMING / FINISHED):
// live first, then upcoming by kickoff, finished by date descending
// (capped, expandable). Suspended events/markets get a chip next to the
// name and a grayed price. Sport + league filters stay.
// Single column; floats above the ui-kit Backdrop.

import { useState } from 'react';
import { useAtomValue } from 'jotai';
import {
  archiveAtom,
  boardEventGroupsAtom,
  groupArchiveByEvent,
  upcomingMarketsAtom,
} from '../feed';
import { ArchiveList } from './archive-list';
import { EventGroupCard, MatchHeader, SportLeague } from './event-group';
import { LiveMarketLine } from './live-market-line';
import { StatsStrip } from './stats-strip';
import { UpcomingMarketLine } from './upcoming-market-line';
import {
  filterGroups,
  leagueOptions,
  matchesFilters,
  sportOptions,
} from './tab-filters';
import { formatCountdown, useNow } from './countdown';
import { RibbonCard, SuspendedChip, ribbonLabel } from './status-ribbon';

const fmtKickoff = (ms: number): string =>
  ms > 0
    ? new Date(ms).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

const selectClass =
  'rounded-full bg-content1 px-3 py-1.5 text-xs outline-none hover:bg-content2';

export function PriceBoard() {
  const board = useAtomValue(boardEventGroupsAtom);
  const upcomingMarkets = useAtomValue(upcomingMarketsAtom);
  const archive = useAtomValue(archiveAtom);
  const now = useNow(1000);

  const [sport, setSport] = useState('');
  const [league, setLeague] = useState('');
  const boardGroups = filterGroups(board, sport, league);
  const archiveGroups = filterGroups(
    groupArchiveByEvent(archive),
    sport,
    league,
  );
  const archiveFiltered = archive.filter((e) =>
    matchesFilters(e.event, sport, league),
  );

  const sports = sportOptions(board, archiveGroups);
  const leagues = leagueOptions([...board, ...archiveGroups], sport);

  return (
    <main className="relative z-10 mx-auto flex w-full max-w-md flex-col gap-6 px-4 py-6 font-sans sm:py-10 md:max-w-2xl">
      <header className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-2">
          <h1 className="text-2xl font-bold tracking-tight">mash.trade</h1>
          <p className="text-xs opacity-50">
            {new Date(now).toLocaleDateString(undefined, {
              weekday: 'short',
              day: 'numeric',
              month: 'short',
            })}
          </p>
        </div>
        <p className="text-sm opacity-60">Live oracle prices</p>
        <StatsStrip />
      </header>

      <div className="flex flex-wrap items-center justify-end gap-2">
        <select
          aria-label="Filter by sport"
          value={sport}
          onChange={(e) => {
            setSport(e.target.value);
            setLeague('');
          }}
          className={selectClass}
        >
          <option value="">All sports</option>
          {sports.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          aria-label="Filter by league"
          value={league}
          onChange={(e) => setLeague(e.target.value)}
          className={selectClass}
        >
          <option value="">All leagues</option>
          {leagues.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-3">
        {boardGroups.length === 0 && archiveGroups.length === 0 && (
          <p className="py-6 text-center text-sm opacity-50">
            No markets right now — next kick-off{' '}
            {upcomingMarkets[0]
              ? formatCountdown(upcomingMarkets[0].event.startTime, now)
              : 'unknown'}
            .
          </p>
        )}
        {boardGroups.map(({ event, markets }) => (
          <RibbonCard key={event.eventId} status={ribbonLabel(event.status)}>
            <EventGroupCard
              header={
                <MatchHeader
                  event={event}
                  score={event.status === 'in_play' ? event.score : null}
                  meta={
                    <>
                      <SportLeague
                        sport={event.sport}
                        league={event.leagueShort || event.league}
                      />
                      {event.status === 'suspended' && <SuspendedChip />}
                      {event.status === 'in_play' ? (
                        <>
                          {fmtKickoff(event.startTime) && (
                            <>
                              <span aria-hidden="true">·</span>
                              <span className="tabular-nums">
                                {fmtKickoff(event.startTime)}
                              </span>
                            </>
                          )}
                          {event.liveTime && (
                            <>
                              <span aria-hidden="true">·</span>
                              <span className="text-primary">
                                {event.liveTime}
                              </span>
                            </>
                          )}
                        </>
                      ) : (
                        event.startTime > 0 && (
                          <>
                            <span aria-hidden="true">·</span>
                            <span className="tabular-nums text-primary">
                              {formatCountdown(event.startTime, now)}
                            </span>
                            <span aria-hidden="true">·</span>
                            <span className="tabular-nums">
                              {new Date(event.startTime).toLocaleString([], {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </>
                        )
                      )}
                      <span aria-hidden="true">·</span>
                      <span>
                        {markets.length}{' '}
                        {markets.length === 1 ? 'market' : 'markets'}
                      </span>
                    </>
                  }
                />
              }
            >
              {markets.map((market) =>
                event.status === 'in_play' ? (
                  <LiveMarketLine key={market.index} market={market} />
                ) : (
                  <UpcomingMarketLine key={market.index} market={market} />
                ),
              )}
            </EventGroupCard>
          </RibbonCard>
        ))}
        <ArchiveList entries={archiveFiltered} />
      </div>
    </main>
  );
}
